import { useCallback, useMemo, useState } from 'react';

export interface IUsePriceProductSelectionParams {
  /** Every Control variant the table currently holds — what select-all picks and what prunes a stale selection. */
  controlVariantBaseIDs: string[];
}

export interface IUsePriceProductSelectionResult {
  selectedBaseIDs: string[];
  isAllSelected: boolean;
  isAnySelected: boolean;
  /** Toggles a whole product: off unless every variant under it is already on. */
  handleToggleSelectedGroup: (baseIDs: string[]) => void;
  handleToggleSelected: (baseID: string) => void;
  handleToggleSelectAll: () => void;
}

/** Selection is per variant throughout; a product row only reads the state of the variants it owns. */
export const usePriceProductSelection = ({
  controlVariantBaseIDs,
}: IUsePriceProductSelectionParams): IUsePriceProductSelectionResult => {
  const [selectedByBaseID, setSelectedByBaseID] = useState<Record<string, boolean>>({});

  // Derived rather than stored, so a removed row drops out of the selection without a sync effect.
  const selectedBaseIDs = useMemo(
    () => controlVariantBaseIDs.filter((baseID) => selectedByBaseID[baseID]),
    [controlVariantBaseIDs, selectedByBaseID],
  );

  const isAllSelected =
    !!controlVariantBaseIDs.length && selectedBaseIDs.length === controlVariantBaseIDs.length;

  const handleToggleSelectedGroup = useCallback((baseIDs: string[]) => {
    setSelectedByBaseID((current) => {
      const isGroupSelected = baseIDs.every((baseID) => current[baseID]);

      return {
        ...current,
        ...Object.fromEntries(baseIDs.map((baseID) => [baseID, !isGroupSelected])),
      };
    });
  }, []);

  const handleToggleSelected = useCallback(
    (baseID: string) => handleToggleSelectedGroup([baseID]),
    [handleToggleSelectedGroup],
  );

  const handleToggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setSelectedByBaseID({});
      return;
    }

    setSelectedByBaseID(Object.fromEntries(controlVariantBaseIDs.map((baseID) => [baseID, true])));
  }, [isAllSelected, controlVariantBaseIDs]);

  return {
    selectedBaseIDs,
    isAllSelected,
    isAnySelected: !!selectedBaseIDs.length,
    handleToggleSelectedGroup,
    handleToggleSelected,
    handleToggleSelectAll,
  };
};
